/**
 * Runtime configuration, fetched from `/config.json` at startup.
 *
 * Deliberately NOT build-time (`VITE_*`) env: those get inlined into the bundle, which
 * would mean one build per environment AND a circular dependency in CD — the console
 * build would need the profile stack's outputs, while the profile stack needs this
 * console's CloudFront URL for CORS + Cognito callbacks. With runtime config we build
 * once and CI writes config.json from the stack outputs.
 *
 * These values are public: the API URL and a Cognito public PKCE client. No secrets.
 */
export interface AppConfig {
  /** Profile service: menu, hours, settings, restaurant info. */
  apiUrl: string;
  /** Order service: the staff order queue. A separate service, so a separate base URL. */
  orderApiUrl: string;
  /** Billing service: calls, charges and transcripts. */
  billingApiUrl: string;
  /**
   * Onboarding service: the signup application and menu uploads.
   *
   * OPTIONAL, deliberately. `config.json` is written by CI from stack outputs, so making this
   * required would fail `isValidConfig` against every already-deployed config file and black the
   * console out with "Not configured yet" until the next deploy. Absent, onboarding says so and the
   * rest of the console works.
   */
  onboardingApiUrl?: string;
  /**
   * Reservation service: table bookings. Optional for the same reason as onboardingApiUrl; absent, the
   * Reservations tab says bookings are unavailable and the rest of the console works.
   */
  reservationApiUrl?: string;
  /** https://cognito-idp.<region>.amazonaws.com/<RestaurantUserPoolId> */
  cognitoAuthority: string;
  cognitoClientId: string;
}

let current: AppConfig | undefined;

/** Redirect target registered with the Cognito app client. */
export const redirectUri = (): string => `${window.location.origin}/auth/callback`;

export function isValidConfig(raw: unknown): raw is AppConfig {
  if (typeof raw !== "object" || raw === null) return false;
  const c = raw as Record<string, unknown>;
  return (
    typeof c.apiUrl === "string" &&
    c.apiUrl.length > 0 &&
    typeof c.orderApiUrl === "string" &&
    c.orderApiUrl.length > 0 &&
    typeof c.billingApiUrl === "string" &&
    c.billingApiUrl.length > 0 &&
    typeof c.cognitoAuthority === "string" &&
    c.cognitoAuthority.length > 0 &&
    typeof c.cognitoClientId === "string" &&
    c.cognitoClientId.length > 0
  );
}

/** Fetch config once. Returns undefined when missing/invalid so the UI can explain. */
export async function loadConfig(): Promise<AppConfig | undefined> {
  if (current) return current;
  try {
    const res = await fetch("/config.json", { cache: "no-store" });
    if (!res.ok) return undefined;
    const raw: unknown = await res.json();
    if (!isValidConfig(raw)) return undefined;
    current = raw;
    return current;
  } catch {
    return undefined; // not deployed yet, or offline
  }
}

/** Config after a successful load. Throws if called too early (a programming error). */
export function getConfig(): AppConfig {
  if (!current) throw new Error("config not loaded");
  return current;
}

/** Test seam. */
export function setConfigForTest(cfg: AppConfig | undefined): void {
  current = cfg;
}
